<template>
  <Transition name="step" mode="out-in">
    <Gallery
      v-if="store.steps[store.currentStep].mounted"
      :gallery="store.steps[store.currentStep].gallery"
      :option="store.steps[store.currentStep].selected.gallery"
      :title="store.steps[store.currentStep].item.title"
      :short-description="shortDescription"
      :bundle-item="store.steps[store.currentStep]"
    />
  </Transition>
  <Transition name="step" mode="out-in">
    <Form
      v-if="store.steps[store.currentStep].mounted"
      :item="store.steps[store.currentStep]"
      :current-step="currentStep"
      :step="step"
      :shell-component="shellComponent"
      :variant="variant"
      :bnpl-message="bnplMessage"
      location="bundle-step"
    />
  </Transition>
</template>

<script setup>
import { onMounted, computed } from 'vue';
import { useProductStore } from './Composables/Store';
import { formatSecondaryProducts } from '@utils/format';
import { fetchStorefront } from '@utils/GraphQLClient';
import { SECONDARY_PRODUCTS_QUERY } from './Composables/Queries';

import Form from './Form.vue';
import Gallery from './Gallery.vue';

const store = useProductStore();
const stepState = store.$state.steps[store.$state.currentStep];
const props = defineProps({
  step: {
    type: Object,
    required: true,
    default: () => ({}),
  },
  currentStep: { type: Number, required: false, default: 0 },
  shellComponent: { type: Object, required: false, default: () => ({}) },
  variant: { type: [Object, Number], required: false, default: () => ({}) },
  bnplMessage: { type: String, required: false, default: '' },
});

const shortDescription = computed(() => {
  const item = props.step.item;
  const hasShortDescription = item?.shortDescription ?? false;
  return hasShortDescription ? item.shortDescription.value : false;
});

const parseSecondaryItems = async (response) => {
  if (!response) return [];

  const haskey = (property, index) => {
    const keyPresent = Object.keys(response).includes(property) ?? false;
    if (!keyPresent) return false;
    const hasValueFromIndex = response[property][index] ?? false;
    return keyPresent && hasValueFromIndex;
  };

  const { handles } = response;

  const items = handles.reduce(async (arr, handle, index) => {
    const merge = haskey('merged', index) ? response?.merged[index] : false;
    if (!merge) return arr;

    const label = haskey('labels', index) ? response?.labels[index] : '';
    let limit = haskey('limits', index) ? response?.limits[index] : false;

    const item = await store.fetchProduct(handle);
    const gallery = await store.fetchGallery(handle);

    const { options, variants } = item;
    const colorIndex = options.findIndex((option) => option.name === 'Color');

    if (colorIndex < 0) return arr;

    const colors = options[colorIndex].values;
    const swatches = limit ? colors.slice(0, limit) : colors;
    limit = swatches.length;

    const filteredSecondaryMedia = gallery.filter((asset) => {
      if (asset.alt !== '' && !asset.alt.includes('FEED')) {
        const alt = asset.alt.split('[')[1].trim().split(']')[0].trim();
        return swatches.includes(alt);
      }
    });
    const filteredMedia = props.step.gallery.filter((asset) => {
      if ((asset.alt !== '' && !asset.alt.includes('FEED')) || asset.sources) {
        const alt = asset.alt.split('[')[1].trim().split(']')[0].trim();
        return !swatches.includes(alt);
      }
    });

    item.limit = limit;
    item.label = label;
    stepState.gallery = [...filteredMedia, ...filteredSecondaryMedia];
    stepState.item.variants = [...stepState.item.variants, ...variants];
    arr.push(item);
    return arr;
  }, []);
  return items;
};

const fetchSecondaryItems = async (handle) => {
  const request = await fetchStorefront({
    variables: {
      handle: handle,
    },
    query: SECONDARY_PRODUCTS_QUERY,
  }).then(async (res) => {
    const response = formatSecondaryProducts(res);
    const hasItems = response?.handles && response?.handles.length > 0;
    if (!hasItems) return [];
    const secondaryItems = await parseSecondaryItems(response);
    return secondaryItems;
  });
  return request;
};

if (!props.step.fetched) {
  stepState.gallery = await store.fetchGallery(props.step.handle);
  stepState.item = await store.fetchProduct(props.step.handle);
  stepState.secondaryItems = await fetchSecondaryItems(props.step.handle);

  if (stepState?.item.promotedOptionGroups) {
    const groups = store.formatPOGs(stepState.item);
    stepState.item.promotedOptionGroups = groups;
  }
}

const tags = props?.step?.item?.tags ?? [];
const options = props?.step?.item?.options ?? [];

store.updateHiddenOptions(tags);
stepState.selected.variant = store.getComponentVariant;
stepState.galleryIndex = tags
  .filter((tag) => tag.includes('Gallery Option:'))
  .map((tag) => tag.split(':')[1].trim())
  .toString();

stepState.modals = tags
  .filter((tag) => tag.includes('PDP Modal Option:'))
  .map((tag) => tag.split(':')[1].trim());

stepState.galleryIndex = options.findIndex(
  (option) => option.name === stepState.galleryIndex
);

const hasSplitLists =
  tags.filter((tag) => tag.includes('Evergreen:')).length > 0;

if (hasSplitLists) {
  const tag = tags.filter((tag) => tag.includes('Evergreen:'))[0];
  const swatchCount = tag ? parseInt(tag.split(':')[1].trim()) : 0;
  stepState.evergreenSwatchCount = swatchCount;
}

if (!props.step.fetched) {
  let index = 0;
  const total = options.length;
  stepState.totalOptions = total;

  do {
    stepState.selected.options.push('');
    index += 1;
  } while (index < total);

  const variants = props?.step?.item?.variants ?? [];
  const matchingIndex = variants.findIndex((variant) => {
    const isNumber = typeof props.step.selected.variant === 'number';
    if (isNumber) {
      const id = parseInt(variant.id.split('ProductVariant/')[1]);
      return id === props.step.selected.variant;
    }
    return variant.id === props.step.selected.variant;
  });

  const galleryIndex = parseInt(props.step.galleryIndex);
  const noMatchFound = matchingIndex && matchingIndex <= -1;

  const matchingVariant = noMatchFound ? variants[0] : variants[matchingIndex];
  const matchingOption = matchingVariant.selectedOptions[galleryIndex];

  if (total <= 1) {
    stepState.selected.available = stepState.item.variants
      .filter((variant) => {
        const isNumber = typeof stepState.selected.variant === 'number';
        if (isNumber) {
          const id = parseInt(variant.id.split('ProductVariant/')[1]);
          return id === stepState.selected.variant;
        }
        return variant.id === stepState.selected.variant;
      })
      .map((variant) => variant.quantityAvailable > 0)[0];
    store.componentSelect(galleryIndex, matchingOption);
  }

  if (total > 1) {
    stepState.selected.matches = variants.filter((variant) => {
      const { selectedOptions } = variant;
      const match = selectedOptions.find(
        (option) => option.value === matchingOption.value
      );
      return match;
    });
  }
  stepState.selected.gallery = matchingOption.value;
  stepState.selected.options[galleryIndex] = matchingOption;
}

const toggleLowerPDPModules = () => {
  //Hides lower half PDP Modules when on the stepped bundle for mobile only. Does not apply to the start building step.
  const pdpBody = document.querySelector('#MainContent');
  store.isBuilding || store.isReviewStep
    ? pdpBody?.setAttribute('data-product-step', true)
    : pdpBody?.removeAttribute('data-product-step', true);
};

onMounted(() => {
  stepState.mounted = true;
  stepState.fetched = true;
  store.$state.steps[store.$state.currentStep] = stepState;
  store.updateURL();
  document
    .querySelector('.js-main-product')
    ?.classList.remove('Product--Loading');
  window.scrollTo(0, 0);
  toggleLowerPDPModules();
});
</script>

<style scoped lang="css">
.step-enter-active,
.step-enter-active *,
.step-leave-active,
.step-leave-active * {
  opacity: 1;
  transition: opacity 400ms ease-in-out;
}

.step-enter-from,
.step-enter-from *,
.step-leave-to,
.step-leave-to * {
  opacity: 0;
}
</style>
